import { Mail, MessageSquare } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import type { CommsSourceRef, EvidenceEvent } from "../../../types/evidence";
import { openDopplerUri } from "../../../utils/dopplerUri";
import {
	commsHref,
	commsMessageAnchor,
	commsThreadName,
	isoDate,
} from "../../../utils/sourceLabels";
import "./EvidenceCards.css";

interface CommsMessageCardProps {
	event: EvidenceEvent;
}

interface DecoratedCommsRef {
	key: string;
	ref: CommsSourceRef;
	threadName: string;
	anchor: string | null;
	sentOn: string | null;
	/** `null` when the ref does not name its projection document. */
	href: string | null;
}

const decorateCommsRefs = (event: EvidenceEvent): DecoratedCommsRef[] =>
	event.source_refs
		.filter((r): r is CommsSourceRef => r.kind === "comms")
		.map((ref, index) => ({
			key: `${ref.message_id}-${index}`,
			ref,
			threadName: commsThreadName(ref),
			anchor: commsMessageAnchor(ref),
			sentOn: isoDate(ref.sent_at),
			href: commsHref(ref),
		}));

/**
 * One email or Slack message as evidence.
 *
 * The visible identity is the thread's name and the message's position in it —
 * never the projection filename the message was ingested into, and never the
 * `thread_id` slug when a real name is available (see `utils/sourceLabels`).
 * Clicking opens the projection document; a ref that does not name one renders
 * as static text rather than a dead link.
 */
const CommsMessageCard: React.FC<CommsMessageCardProps> = ({ event }) => {
	const decorated = useMemo(() => decorateCommsRefs(event), [event]);
	// Slack and email share this card; the icon follows the first ref, since a
	// single evidence event describes one read of one conversation.
	const isSlack = decorated[0]?.ref.source_system === "slack";
	const Icon = isSlack ? MessageSquare : Mail;

	const handleOpen = useCallback(
		(href: string) => (e: React.MouseEvent) => {
			e.preventDefault();
			void openDopplerUri(href);
		},
		[],
	);

	return (
		<article className="evidence-card">
			<header className="evidence-card-header">
				<Icon className="evidence-card-header-icon" aria-hidden />
				<div className="evidence-card-header-body">
					<h3 className="evidence-card-title">{event.title}</h3>
					<p className="evidence-card-summary">{event.summary}</p>
				</div>
			</header>
			<ul className="evidence-card-list">
				{decorated.map(({ key, ref, threadName, anchor, sentOn, href }) => {
					const locator = (
						<>
							<span className="evidence-card-filename">{threadName}</span>
							{anchor && <span className="evidence-card-meta">{anchor}</span>}
							{ref.sender && (
								<span className="evidence-card-meta">· {ref.sender}</span>
							)}
							{sentOn && <span className="evidence-card-meta">· {sentOn}</span>}
						</>
					);
					return (
						<li key={key} className="evidence-card-row">
							{href ? (
								<a
									href={href}
									onClick={handleOpen(href)}
									className="evidence-card-row-link"
								>
									{locator}
								</a>
							) : (
								<div className="evidence-card-row-static">{locator}</div>
							)}
							{ref.excerpt && (
								<blockquote className="evidence-card-blockquote">
									{ref.excerpt}
								</blockquote>
							)}
						</li>
					);
				})}
			</ul>
		</article>
	);
};

export default React.memo(CommsMessageCard);
